import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Router } from "@angular/router";
import { JourneySearchStore } from "src/app/features/store/journey-search-store";
import { Button } from "src/app/shared/components/utils/button/button";
import { Tile } from "src/app/shared/components/tile/tile";
import { Loader } from "src/app/shared/components/loader/loader";
import { TranslocoModule } from "@jsverse/transloco";
import { Icon } from "src/app/shared/components/utils/icon/icon";
import { CardResult } from "src/app/shared/components/card-result/card-result";
import { JourneyBreadcrumb } from "../journey-breadcrumb/journey-breadcrumb";
import {
  TileInterface,
  VideoTileDto,
  TileStatusEnum,
} from "src/app/core/interfaces/tile-interface";
import { SafeHtmlPipe } from "../../../../../shared/pipes/safe-html.pipe";

import { LanguageFilter } from "src/app/shared/components/language-filter/language-filter";
import { Toggle } from "src/app/shared/components/toggle/toggle";
import { UserStore } from "src/app/features/store/user-store";
import { TilesApiService } from "src/app/core/services/tiles/tiles-service";
import { ProgressionBar } from "../progression-bar/progression-bar";
import { LanguageService } from "src/app/core/services/language-service";
import { FavoriteTileErrorModal } from "src/app/shared/components/favorite-tile-error-modal/favorite-tile-error-modal";
import { Modal } from "src/app/shared/components/utils/modal/modal";
import { PianoAnalyticsService } from "src/app/core/services/piano-analytics";

@Component({
  selector: "app-clarify-project-page",
  imports: [
    Button,
    Tile,
    Toggle,
    Loader,
    Icon,
    TranslocoModule,
    CardResult,
    JourneyBreadcrumb,
    SafeHtmlPipe,
    LanguageFilter,
    ProgressionBar,
    FavoriteTileErrorModal,
    Modal,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./clarify-project-page.html",
})
export class ClarifyProjectPage implements OnDestroy {
  store = inject(JourneySearchStore);
  userStore = inject(UserStore);
  tilesApi = inject(TilesApiService);
  userRole = computed(() => this.userStore.getUserPermission());

  router = inject(Router);
  private readonly languageService = inject(LanguageService);
  private readonly piano = inject(PianoAnalyticsService);

  isJourneyImmature = computed(() => this.store.isJourneyImmature());
  tilesProject = computed(() => this.store.tilesProject());
  tilesProjectToShow = computed(() => {
    const sourceTiles = this.modeDraft()
      ? this.tilesProject()
      : this.tilesProject().filter(
          (tile) => tile.status === TileStatusEnum.Approved
        );

    return sourceTiles.map((tile, index) => ({
      ...tile,
      size: this.tilesApi.giveSizeToTiles(index),
    }));
  });
  videos = computed(() => this.store.videos());
  videosLoading = signal<boolean>(false);
  tilesLoading = computed(() => this.store.tilesLoading());
  extraVideosLoading = signal<boolean>(false);
  questionnaireTileLoading = signal<boolean>(false);

  languagesSelected = signal<string[]>([]);

  shownVideos = signal<VideoTileDto[]>([]);

  availableLanguages = computed(() => {
    const results = this.store.videos();
    const languages = new Set<string>();

    results.forEach((video) => {
      video.languages.forEach((lang) => {
        const matchedLanguages = this.store
          .languageFilters()
          .filters.filter(
            (languageFilter) => languageFilter.translation === lang
          );
        if (matchedLanguages.length > 0) {
          matchedLanguages.forEach((matchedLanguage) => {
            languages.add(matchedLanguage.translation);
          });
        }
      });
    });

    return Array.from(languages).sort((a, b) => a.localeCompare(b));
  });

  modeDraft = signal<boolean>(false);

  isModalOpen = signal<boolean>(false);

  isRedirectionExternal = signal<boolean>(false);

  effectiveTextDisclaimer = signal<string | undefined>("");

  currentTileToRedirect = signal<TileInterface | undefined>(undefined);

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();

      untracked(async () => {
        await this.store.loadJourneyFilters();
        await this.loadClarifyProjectTiles();
      });
    });
  }

  private async loadClarifyProjectTiles(): Promise<void> {
    if (!this.isJourneyImmature()) {
      this.videosLoading.set(true);
      this.questionnaireTileLoading.set(true);
    }

    try {
      await this.store.loadTilesByTheme();
    } finally {
      this.questionnaireTileLoading.set(false);
    }

    if (!this.isJourneyImmature()) {
      try {
        await this.store.loadVideos();
      } finally {
        this.shownVideos.set(this.store.videos());
        this.videosLoading.set(false);
      }
    }
  }

  clickOnTile(questionnaireTile: TileInterface) {
    this.isRedirectionExternal.set(questionnaireTile.isExternalLink);
    this.currentTileToRedirect.set(questionnaireTile);
    if (questionnaireTile.isExternalLink) {
      if (
        this.tilesApi.hasMeaningfulTextContent(questionnaireTile.textDisclaimer)
      ) {
        this.effectiveTextDisclaimer.set(questionnaireTile.textDisclaimer);
      } else {
        this.effectiveTextDisclaimer.set(undefined);
      }
      this.isModalOpen.set(true);
    } else {
      this.goToLink(questionnaireTile);
    }
  }

  onLanguagesChanged(selectedLanguages: string[]): void {
    this.languagesSelected.set(selectedLanguages);

    if (selectedLanguages.length === 0) {
      this.shownVideos.set(this.store.videos());
    } else {
      this.shownVideos.set(
        this.store
          .videos()
          .filter((video) =>
            selectedLanguages.some((lang) => video.languages.includes(lang))
          )
      );
    }
  }

  async loadMoreVideoTiles(): Promise<void> {
    this.store.videosPagination().start =
      this.store.videosPagination().start + 1;
    try {
      this.extraVideosLoading.set(true);
      await this.store.loadMoreVideos();
      this.shownVideos.set(this.store.videos());
    } finally {
      this.extraVideosLoading.set(false);
    }
  }

  redirectToJourneyResearch(): void {
    this.router.navigate(["/journey/search"]);
  }

  openRequestErrorModal(): void {
    this.store.setIsRequestErrorModalOpen(true);
  }

  closeRequestErrorModalAndRedirectToJobResearch(): void {
    this.store.setIsRequestErrorModalOpen(false);
    this.redirectToJourneyResearch();
  }

  closeRequestErrorModal(): void {
    this.store.setIsRequestErrorModalOpen(false);
  }

  toggleDraftMode(): void {
    this.modeDraft.set(!this.modeDraft());
  }

  closeModal(): void {
    this.currentTileToRedirect.set(undefined);
    this.isRedirectionExternal.set(false);
    this.isModalOpen.set(false);
  }

  goToLinkForExternalRedirection(): void {
    const tileToRedirect = this.currentTileToRedirect();
    if (!tileToRedirect) {
      return;
    }

    const link = tileToRedirect.ctaLink;
    if (link) {
      window.open(link, "_blank", "noopener,noreferrer");
    }
    this.piano.sendEvent("tiles.selection", {
      tile_name: tileToRedirect.title,
    });
    if (tileToRedirect.isExternalLink) {
      this.closeModal();
    }
  }

  goToLink(questionnaireTile: TileInterface): void {
    const link = questionnaireTile?.ctaLink;
    if (link) {
      this.router.navigate([link]);
    }
    this.piano.sendEvent("tiles.selection", {
      tile_name: questionnaireTile.title,
    });
    if (questionnaireTile.isExternalLink) {
      this.closeModal();
    }
  }

  ngOnDestroy() {
    document.body.style.overflow = "";
  }
}
